import { useQuery } from "@apollo/client";
import { gql } from "generated-graphql";
import { GetCohortQuery } from "generated-graphql/graphql";
import { useParams } from "react-router-dom";

export const GET_COHORT = gql(`
query GetCohort($cohortId: ID!) {
  cohort(id: $cohortId) {
    id
    type
    tenantId
    allowsMultipleCohorts
    tenant {
      id
      name
    }
    status
    reportingYear
    alternateIdValue
    fdId
    fireDepartment {
      id
      name
      mailingAddress
      mailingAddressLine2
      city
      state
      zip
      email
    }
    lepcId
    lepc {
      id
      name
      mailingAddress
      mailingAddressLine2
      city
      state
      zip
      email
    }
    credentialUsername
    state
    chemicalCounts {
      total
      ehs
    }
    portalCredential {
      type
      tech
      url
      message
      credential {
        id
        isEncampOwned
        kind
        password
        securityQuestions {
          question
          answer
        }
        username
        lastVerificationCheckAt
        lastVerificationSuccessAt
        verificationStatus
        tenantId
        usesCombinedInvoicing
        usesRcoCredential
      }
    }
    createdAt
    updatedAt
    members {
      id
      status
      tierIIReportId
      tierIIReport {
        id
        fulfillmentNote
      }
      facility {
        id
        name
        state
        city
        tenant {
          id
          name
        }
      }
    }
    relatedCohorts {
      id
      status
      totalMemberCount
      workflowStatus
    }
    workflows {
      id
      status
      type
      activities {
        id
        type
        status
        executionPlan
        description
        jobs {
          id
          status
          task
          action
          taskUrl
          createdAt
          updatedAt
        }
      }
    }
    currentWorkflow {
      id
      status
      tags {
        id
        name
      }
      comments {
        id
        comment
        createdAt
        updatedAt
        createdBy
        user {
          id
          email
          person {
            first
            last
          }
        }
      }
      activities {
        id
        type
        status
        executionPlan
        description
        championId
        nonChampionIds
        cohortFacilityIds
        reportDetailsExtended
        output
        inputs {
          id
          activityId
          label
          shortName
          value
          required
          type
        }
        documents {
          document {
            id
            title
            description
            storageLink
            documentType
            fileExtension
            updatedAt
            createdAt
            sizeMegabytes
            documentTags {
              id
              name
              type
            }
          }
        }
        currentJob {
          id
          status
          task
          action
          taskUrl
          createdAt
        }
        jobs {
          id
          status
          task
          action
          taskUrl
          createdAt
          updatedAt
        }
        payments {
          id
          status
          paidAt
          tenantId
          amount
          lobMail {
            id
            lobId
            status
            type
          }
        }
        communications {
          id
          status
          jobId
          communicationDocuments {
            id
            sequence
            document {
              id
              title
              documentType
              storageLink
            }
          }
        }
      }
      currentActivityId
      currentActivity {
        id
        type
        status
        executionPlan
        description
        assigneeId
        currentJobId
        nonChampionIds
        currentJob {
          id
          taskArn
          taskUrl
          status
          task
          action
          createdAt
        }
        assignee {
          id
          email
          person {
            first
            last
          }
        }
        documents {
          document {
            id
            title
            description
            storageLink
            documentType
            fileExtension
            updatedAt
            createdAt
            sizeMegabytes
            documentTags {
              id
              name
              type
            }
          }
        }
      }
    }
  }
}`);

type MinimalOptions = {
  onCompleted?: (data: GetCohortQuery) => void;
};

export function useCohortDetails(options?: MinimalOptions) {
  const { cohortId } = useParams<{ cohortId: string }>();

  const query = useQuery(GET_COHORT, {
    variables: {
      cohortId: cohortId ?? "",
    },
    skip: !cohortId,
    onCompleted: options?.onCompleted,
  });

  if (query.error) throw query.error;

  return query;
}
